import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import Dashboard from './pages/Dashboard';
import AlertsPage from './pages/AlertsPage';
import OCIDashboard from './pages/OCIDashboard';
import OCIInstances from './pages/OCIInstances';
import OCICosts from './pages/OCICosts';
import OCIStorageDashboard from './pages/OCIStorageDashboard';
import Hosts from './pages/Hosts';
import Clients from './pages/Clients';
import Products from './pages/Products';
import RegionDashboard from './pages/RegionDashboard';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import UserManagement from './pages/UserManagement';
import AuditLogs from './pages/AuditLogs';


// Import ESDL pages
import EsdlMonitoringDashboard from './pages/EsdlMonitoring/Dashboard';
import HostDetails from './pages/EsdlMonitoring/HostDetails';

// Import OpenSearch pages
import OpenSearchMonitoringPage from './pages/OpenSearchMonitoring/Dashboard';

// Import Production Redirections page
import ProductionRedirections from './pages/ProductionRedirections';

// Import Mixpanel components
import MixpanelAuthWrapper from './components/MixpanelAuthWrapper';
import SimpleExample from './components/SimpleExample';
import PageTrackingDemo from './components/PageTrackingDemo';

// Import layout
import MainLayout from './layouts/MainLayout';

// Import styles
import './custom-style.css';

// Create a custom theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3a86ff', // Professional blue
      light: '#61a0ff',
      dark: '#2970d6',
    },
    secondary: {
      main: '#4cc9bd', // Teal
      light: '#70d8cf',
      dark: '#3aa89e',
    },
    background: {
      paper: '#1c1c1c',
      default: '#0a0a0a',
      card: '#262626',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
    info: {
      main: '#2196f3',
    },
  },
});

function App() {
  const [loading, setLoading] = useState(true);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <MixpanelAuthWrapper>
          <AppWithAuth loading={loading} setLoading={setLoading} />
        </MixpanelAuthWrapper>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Create a nested component that can access the auth context
function AppWithAuth({ loading, setLoading }) {
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    // We're no longer checking session IDs, just setting loading to false
    // The ProtectedRoute component will handle authentication checks
    setLoading(false);
    
    console.log("App initialization - Authentication status:", isAuthenticated);
  }, [isAuthenticated, setLoading]);
  
  return (
        <AppDataProvider>
          <Router>
            <Routes>
          {/* Public route */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Protected routes within MainLayout */}
          <Route path="/" element={<MainLayout />}>
            {/* Dashboard - Visible to all authenticated users */}
            <Route index element={
              <ProtectedRoute requiredPermission="VIEW_DASHBOARD">
                <Dashboard />
              </ProtectedRoute>
            } />
            
            {/* Hosts - Only for users with VIEW_HOSTS permission */}
            <Route path="hosts" element={
              <ProtectedRoute requiredPermission="VIEW_HOSTS" redirectTo="/unauthorized">
                <Hosts />
              </ProtectedRoute>
            } />
            
            {/* Clients - Only for users with VIEW_CLIENTS permission */}
            <Route path="clients" element={
              <ProtectedRoute requiredPermission="VIEW_CLIENTS" redirectTo="/unauthorized">
                <Clients />
              </ProtectedRoute>
            } />
            
            {/* Hosts - Only for users with VIEW_HOSTS permission */}
            <Route path="region/:regionId" element={
              <ProtectedRoute requiredPermission="VIEW_HOSTS" redirectTo="/unauthorized">
                <RegionDashboard />
              </ProtectedRoute>
            } />

            {/* Products - Only for users with VIEW_CLIENTS permission */}
            <Route path="products" element={
              <ProtectedRoute requiredPermission="VIEW_CLIENTS" redirectTo="/unauthorized">
                <Products />
              </ProtectedRoute>
            } />
            
            {/* Alerts - Only for users with VIEW_ALERTS permission */}
            <Route path="alerts" element={
              <ProtectedRoute requiredPermission="VIEW_ALERTS" redirectTo="/unauthorized">
                <AlertsPage />
              </ProtectedRoute>
            } />
            
            {/* OCI - Various permissions for different sections */}
            <Route path="oci" element={
              <ProtectedRoute requiredPermission="VIEW_OCI_DASHBOARD" redirectTo="/unauthorized">
                <OCIDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="oci/instances" element={
              <ProtectedRoute requiredPermission="VIEW_OCI_INSTANCES" redirectTo="/unauthorized">
                <OCIInstances />
              </ProtectedRoute>
            } />
            
            <Route path="oci/costs" element={
              <ProtectedRoute requiredPermission="VIEW_OCI_COSTS" redirectTo="/unauthorized">
                <OCICosts />
              </ProtectedRoute>
            } />
            
            <Route path="oci/storage" element={
              <ProtectedRoute requiredPermission="VIEW_OCI_STORAGE" redirectTo="/unauthorized">
                <OCIStorageDashboard />
              </ProtectedRoute>
            } />
            
            {/* ESDL Monitoring Routes */}
            <Route path="esdl-monitoring" element={
              <ProtectedRoute requiredPermission="VIEW_ESDL_MONITORING" redirectTo="/unauthorized">
                <EsdlMonitoringDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="esdl-monitoring/host/:hostname" element={
              <ProtectedRoute requiredPermission="VIEW_ESDL_MONITORING" redirectTo="/unauthorized">
                <HostDetails />
              </ProtectedRoute>
            } />
            
            {/* OpenSearch Monitoring Route */}
            <Route path="opensearch-monitoring" element={
              <ProtectedRoute requiredPermission="VIEW_OPENSEARCH" redirectTo="/unauthorized">
                <OpenSearchMonitoringPage />
              </ProtectedRoute>
            } />
            
            {/* Settings - Available to all authenticated users */}
            <Route path="settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            
            <Route path="users" element={
              <ProtectedRoute requiredPermission="MANAGE_USERS" redirectTo="/unauthorized">
                <UserManagement />
              </ProtectedRoute>
            } />
            
            {/* Audit Logs - Only for users with VIEW_AUDIT_LOGS permission */}
            <Route path="audit-logs" element={
              <ProtectedRoute requiredPermission="VIEW_AUDIT_LOGS" redirectTo="/unauthorized">
                <AuditLogs />
              </ProtectedRoute>
            } />
            
            {/* Production Redirections - RD and Admin can view, only Admin can manage */}
            <Route path="production-redirections" element={
              <ProtectedRoute requiredPermission="VIEW_PRODUCTION_REDIRECTIONS" redirectTo="/unauthorized">
                <ProductionRedirections />
              </ProtectedRoute>
            } />
            
            {/* Mixpanel Test Route - Simple tracking demo */}
            <Route path="mixpanel-demo" element={<SimpleExample />} />
            
            {/* Page Tracking Demo Route - Shows improved tracking */}
            <Route path="page-tracking-demo" element={<PageTrackingDemo />} />
            
            {/* Catch-all for invalid routes */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            
           
          </Router>
        </AppDataProvider>
  );
}

export default App;